import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FiClock } from 'react-icons/fi';

export const RESERVATION_WARNING_SECONDS = 120;

interface TimerChipProps {
  expiresAt: string | Date | null;
  onExpire?: () => void;
  // Para los dos minutos: la página muestra su aviso.
  onWarning?: () => void;
  label?: string;
  // Desde cuántos segundos se avisa (ámbar y onWarning). Por defecto, dos minutos.
  warningSeconds?: number;
  className?: string;
}

const format = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const secondsLeft = (expiresAt: string | Date | null) => (expiresAt
  ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000))
  : 0);

/**
 * Tiempo que queda de la reserva. Pasa a ámbar a los dos minutos. Solo se
 * anuncia a los lectores de pantalla en ese momento, no cada segundo.
 */
const TimerChip = ({
  expiresAt, onExpire, onWarning, label = 'Tiempo restante', warningSeconds = RESERVATION_WARNING_SECONDS, className,
}: TimerChipProps) => {
  const [left, setLeft] = useState(() => secondsLeft(expiresAt));
  // Los avisos van una vez por vencimiento: al renovar la reserva cambia
  // expiresAt y se vuelven a poder dar. Por ref, para no repetirlos en cada
  // render del padre (que pasa los callbacks como funciones nuevas).
  const notified = useRef<{ key: string, warned: boolean, expired: boolean }>({ key: '', warned: false, expired: false });
  const callbacks = useRef({ onExpire, onWarning });
  callbacks.current = { onExpire, onWarning };

  useEffect(() => {
    setLeft(secondsLeft(expiresAt));
    const timer = setInterval(() => setLeft(secondsLeft(expiresAt)), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  useEffect(() => {
    if (!expiresAt) {
      return;
    }
    const key = String(new Date(expiresAt).getTime());
    if (notified.current.key !== key) {
      notified.current = { key, warned: false, expired: false };
    }
    // Se mide contra expiresAt y no contra `left`, que en el render en que
    // cambia la reserva todavía tiene el valor de la vieja.
    const now = secondsLeft(expiresAt);
    if (now === 0 && !notified.current.expired) {
      notified.current.expired = true;
      callbacks.current.onExpire?.();
    } else if (now > 0 && now <= warningSeconds && !notified.current.warned) {
      notified.current.warned = true;
      callbacks.current.onWarning?.();
    }
  }, [left, expiresAt, warningSeconds]);

  if (!expiresAt) {
    return null;
  }
  const urgent = left <= warningSeconds;
  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold tabular-nums',
        urgent ? 'bg-warning-500/15 text-warning-800 dark:text-warning-300' : 'bg-surface-secondary text-text-primary',
        className,
      )}
    >
      <FiClock className="h-4 w-4" aria-hidden />
      <span aria-hidden>{format(left)}</span>
      <span className="sr-only" aria-live={urgent ? 'polite' : 'off'}>
        {`${label}: ${urgent ? `quedan menos de ${Math.ceil(warningSeconds / 60)} minutos` : `${Math.ceil(left / 60)} minutos`}`}
      </span>
    </span>
  );
};

export default TimerChip;
